import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  CheckCircle,
  Error,
  Email,
  Schedule,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Mensagem } from '../../services/mensagemService';

interface EmailViewMsgProps {
  open: boolean;
  onClose: () => void;
  mensagem: Mensagem | null;
}

const EmailViewMsg: React.FC<EmailViewMsgProps> = ({
  open,
  onClose,
  mensagem,
}) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      enviado: 'Enviado',
      aguardando_envio: 'Aguardando Envio',
      falhou: 'Falhou',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enviado':
        return 'success';
      case 'aguardando_envio':
        return 'warning';
      case 'falhou':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enviado':
        return <CheckCircle fontSize="small" />;
      case 'aguardando_envio':
        return <Schedule fontSize="small" />;
      case 'falhou':
        return <Error fontSize="small" />;
      default:
        return null;
    }
  };

  // Extrair assunto do conteúdo (se houver)
  const extractSubject = (conteudo: string) => {
    if (conteudo.startsWith('Assunto: ')) {
      const linhas = conteudo.split('\n\n');
      if (linhas.length > 0 && linhas[0]) {
        return linhas[0].replace('Assunto: ', '');
      }
    }
    return 'Sem assunto';
  };

  const extractBody = (conteudo: string) => {
    if (conteudo.startsWith('Assunto: ')) {
      const linhas = conteudo.split('\n\n');
      return linhas.slice(1).join('\n\n');
    }
    return conteudo;
  };

  if (!mensagem || mensagem.tipo !== 'email') {
    return null;
  }

  const assunto = extractSubject(mensagem.conteudo);
  const corpo = extractBody(mensagem.conteudo);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header do Email */}
      <Box
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Visualização de E-mail
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Conteúdo do Email */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper elevation={2} sx={{ p: 3, bgcolor: 'white' }}>
          {/* Cabeçalho do Email */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {assunto}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  De:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {mensagem.usuario?.nome || 'Sistema'} &lt;{mensagem.usuario?.usuario || 'sistema@sysup.com'}&gt;
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  Para:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {mensagem.cliente?.nome || mensagem.destino} &lt;{mensagem.destino}&gt;
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  Data:
                </Typography>
                <Typography variant="body2">
                  {formatDate(mensagem.data_envio || mensagem.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                  Status:
                </Typography>
                <Chip
                  icon={getStatusIcon(mensagem.status) || undefined}
                  label={formatStatus(mensagem.status)}
                  color={getStatusColor(mensagem.status) as any}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Corpo do Email */}
          <Box
            sx={{
              minHeight: '200px',
              bgcolor: '#fafafa',
              p: 2,
              borderRadius: 1,
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#212121',
                lineHeight: 1.6,
              }}
            >
              {corpo}
            </Typography>
          </Box>

          {/* Erro (se houver) */}
          {mensagem.erro_mensagem && (
            <Box sx={{ mt: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                  p: 2,
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#d32f2f',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Error sx={{ fontSize: '1.2rem' }} />
                  <strong>Erro:</strong> {mensagem.erro_mensagem}
                </Typography>
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Footer com informações */}
      <Box
        sx={{
          bgcolor: '#f5f5f5',
          p: 2,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
              Tentativas: {mensagem.tentativas}
            </Typography>
          </Grid>
          {mensagem.usuario && (
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" color="textSecondary">
                Enviado por: {mensagem.usuario.nome} ({mensagem.usuario.usuario})
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Dialog>
  );
};

export default EmailViewMsg;

